import { useRef, type ChangeEvent } from 'react';
import type { PendingFile } from './FilePreview';
import type { AttachedFile } from '../types';
import { FILE_ACCEPT_STRING, getSupportedFormatsText } from '../config/fileUpload';

// Unified file type for display
interface DisplayFile {
  id: string;
  filename: string;
  fileType: string;
  fileSize: number;
  status: 'pending' | 'uploading' | 'uploaded' | 'sent' | 'error';
  progress?: number;
  error?: string;
  canRemove: boolean; // Only pending files can be removed
}

interface FilesViewProps {
  pendingFiles: PendingFile[];
  sentFiles: AttachedFile[]; // Files from sent messages
  onRemove: (id: string) => void;
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
}

// Trash icon
const TrashIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

function getFileIcon(type: string): string {
  if (type.startsWith('image/')) return '🖼️';
  if (type.includes('pdf')) return '📄';
  if (type.includes('spreadsheet') || type.includes('excel') || type.includes('csv')) return '📊';
  if (type.includes('dwg') || type.includes('dxf') || type.includes('svg')) return '📐';
  return '📎';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Convert pending files and sent files to unified display format
function toDisplayFiles(pendingFiles: PendingFile[], sentFiles: AttachedFile[]): DisplayFile[] {
  const pending: DisplayFile[] = pendingFiles.map((pf) => ({
    id: pf.id,
    filename: pf.file.name,
    fileType: pf.file.type,
    fileSize: pf.file.size,
    status: pf.status,
    progress: pf.progress,
    error: pf.error,
    canRemove: true,
  }));

  const sent: DisplayFile[] = sentFiles.map((f) => ({
    id: f.file_id,
    filename: f.filename,
    fileType: f.file_type,
    fileSize: f.file_size,
    status: 'sent' as const,
    canRemove: false,
  }));

  // Pending files first, then sent files (most recent first)
  return [...pending, ...sent];
}

export function FilesView({
  pendingFiles,
  sentFiles,
  onRemove,
  onFilesSelected,
  disabled = false,
}: FilesViewProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const displayFiles = toDisplayFiles(pendingFiles, sentFiles);

  const handleAddFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []).slice(0, 5);
    const maxSize = 25 * 1024 * 1024;
    const validFiles = selectedFiles.filter((f) => f.size <= maxSize);

    if (validFiles.length > 0) {
      onFilesSelected(validFiles);
    }
    e.target.value = '';
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        className="hidden"
        accept={FILE_ACCEPT_STRING}
      />

      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Session Files</h2>
        <button
          onClick={handleAddFiles}
          disabled={disabled}
          className="px-3 py-1.5 text-sm text-blue-600 border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          + Add
        </button>
      </div>

      {/* Files list */}
      <div className="flex-1 overflow-y-auto p-3">
        {displayFiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-sm text-gray-500 px-4">
            <p className="mb-2">No files attached</p>
            <p className="text-xs text-gray-400">
              Click "+ Add" above or drag files to the chat window
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {displayFiles.map((df) => (
              <div
                key={df.id}
                className={`
                  flex items-center gap-3 px-3 py-2 rounded border
                  ${df.status === 'error'
                    ? 'bg-red-50 border-red-200'
                    : df.status === 'sent'
                      ? 'bg-green-50 border-green-200'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }
                `}
              >
                <span className="text-2xl">{getFileIcon(df.fileType)}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {df.filename}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-2">
                    <span>{formatFileSize(df.fileSize)}</span>
                    {df.status === 'uploading' && df.progress !== undefined && (
                      <span className="text-blue-600">• {df.progress}%</span>
                    )}
                    {df.status === 'uploaded' && (
                      <span className="text-green-600">• ✓ Ready</span>
                    )}
                    {df.status === 'sent' && (
                      <span className="text-green-700">• ✓ Sent</span>
                    )}
                    {df.status === 'error' && (
                      <span className="text-red-600" title={`Supported: ${getSupportedFormatsText()}`}>
                        • ✗ {df.error || 'Failed'}
                      </span>
                    )}
                  </div>
                  {/* Progress bar */}
                  {df.status === 'uploading' && df.progress !== undefined && (
                    <div className="w-full h-1.5 bg-gray-200 rounded mt-1.5">
                      <div
                        className="h-full bg-blue-500 rounded transition-all duration-200"
                        style={{ width: `${df.progress}%` }}
                      />
                    </div>
                  )}
                </div>
                {/* Delete button - only for pending files */}
                {df.canRemove && (
                  <button
                    onClick={() => onRemove(df.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                    aria-label={`Delete ${df.filename}`}
                    title="Delete file"
                  >
                    <TrashIcon />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
